import React from "react";
import useReunionGame from "../hooks/useReunionGame";
import ReunionLanguageSelect from "../ReunionLanguageSelect";

import Level1Question from "./Level1Question";
import Level2PictureId from "./Level2PictureId";
import Level3Memory from "./Level3Memory";
import Level4Match from "./Level4Match";
import ReunionResults from "./ReunionResults";

// This replaces the old `<TinyThink authUser={authState} />` import in
// ReunionLive.jsx, which pointed at a file that doesn't exist in the repo.
// This component owns the whole in-game experience for one participant.

const MECHANIC_COMPONENTS = {
  textQA: Level1Question, // covers both Level 1 and Level 5 (Level 5 is textQA too)
  pictureId: Level2PictureId,
  memoryPos: Level3Memory,
  match: Level4Match,
};

const ReunionGameHome = ({ eventId }) => {
  const { session, remainingSeconds, submitting, startGame, submitAnswer } =
    useReunionGame(eventId);

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center text-emerald-300">
        Loading your game...
      </div>
    );
  }

  if (session.waiting) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-4">
        <div>
          <div className="text-6xl mb-4">🟡</div>
          <h2 className="text-2xl font-bold">Waiting for the organiser to start...</h2>
        </div>
      </div>
    );
  }

  if (session.needsLanguage) {
    return <ReunionLanguageSelect onSelect={startGame} />;
  }

  if (session.completed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <ReunionResults score={session.score} correctCount={session.correctCount} />
      </div>
    );
  }

  if (session.eventIsPaused) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-4">
        <div>
          <div className="text-6xl mb-4 animate-pulse">⏸</div>
          <h2 className="text-2xl font-bold">Game Paused</h2>
          <p className="text-emerald-300 mt-2">Your timer is frozen — hang tight.</p>
        </div>
      </div>
    );
  }

  if (!session.question) {
    return (
      <div className="min-h-screen flex items-center justify-center text-emerald-300">
        Loading next question...
      </div>
    );
  }

  const ScreenComponent = MECHANIC_COMPONENTS[session.question.mechanic];

  if (!ScreenComponent) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-300">
        Unknown question type "{session.question.mechanic}" — check the seed
        data's `mechanic` field.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#041b18] via-[#072420] to-[#020808] flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full mb-4 max-w-3xl mx-auto flex justify-between text-sm text-emerald-300">
        <span>Level {session.level} of 5</span>
        <span>
          Question {session.questionIndex + 1} of {session.totalInLevel}
        </span>
      </div>

      <ScreenComponent
        question={session.question}
        remainingSeconds={remainingSeconds}
        submitting={submitting}
        onSubmit={submitAnswer}
      />
    </div>
  );
};

export default ReunionGameHome;
